import React, { useState, useEffect } from 'react';
import { RecordingData, dashboardApi } from '@/lib/services/dashboard-api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Play, ArrowRight, Clock, HardDrive, Download, FileVideo } from 'lucide-react';
import { formatDistanceToNowSafe } from '@/lib/utils/date';
import { StreamRecordingDetails } from '@/components/admin/stream-recording-details';
import { MediaImage } from '@/components/ui/media-image';

interface RecentRecordingsListProps {
  onViewMore?: () => void;
  maxRecordings?: number;
}

export function RecentRecordingsList({ onViewMore, maxRecordings = 5 }: RecentRecordingsListProps) {
  const [recordings, setRecordings] = useState<RecordingData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<RecordingData | null>(null);

  useEffect(() => {
    fetchRecentRecordings();
  }, []);

  const fetchRecentRecordings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await dashboardApi.getRecentRecordings(maxRecordings);
      setRecordings(data);
    } catch (err) {
      console.error('Error fetching recent recordings:', err);
      setError('Failed to load recent recordings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (streamId: number) => {
    try {
      const downloadUrl = await dashboardApi.downloadRecording(streamId);
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading recording:', error);
      toast.error('Failed to download recording');
    }
  };

  const handlePlayRecording = async (streamId: number) => {
    try {
      const playbackUrl = await dashboardApi.getRecordingPlaybackUrl(streamId);
      if (playbackUrl) {
        window.open(playbackUrl, '_blank');
      } else {
        // Fall back to signed download URL when playback URL is unavailable.
        const downloadUrl = await dashboardApi.downloadRecording(streamId);
        if (downloadUrl) {
          window.open(downloadUrl, '_blank');
        } else {
          toast.error('No playback URL available for this recording');
        }
      }
    } catch (error) {
      console.error('Error playing recording:', error);
      toast.error('Failed to play recording');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'ready':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'failed':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const formatDuration = (duration: number) => {
    if (!duration) return 'N/A';
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileVideo className="h-5 w-5" />
            Recent Recordings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(maxRecordings)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileVideo className="h-5 w-5" />
            Recent Recordings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRecentRecordings}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileVideo className="h-5 w-5" />
          Recent Recordings
        </CardTitle>
      </CardHeader>
      <CardContent>
        {recordings.length === 0 ? (
          <div className="text-center py-6">
            <FileVideo className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No recent recordings</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recordings.map((recording) => (
              <div key={recording.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                  {recording.thumbnailUrl ? (
                    <MediaImage
                      src={recording.thumbnailUrl}
                      alt={recording.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <FileVideo className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      type="button"
                      className="text-sm font-medium truncate hover:text-primary hover:underline focus:outline-none focus:underline cursor-pointer text-left"
                      onClick={() => {
                        setSelectedRecording(recording);
                        setDetailsDialogOpen(true);
                      }}
                      title="View recording details"
                    >
                      {recording.title}
                    </button>
                    <Badge
                      variant="secondary"
                      className={`text-xs px-1.5 py-0.5 ${getStatusColor(recording.status)}`}
                    >
                      {recording.status}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDuration(recording.duration)}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <HardDrive className="h-3 w-3" />
                      <span>{formatFileSize(recording.fileSize)}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNowSafe(recording.createdAt)}
                    </div>
                  </div>


                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(recording.streamId)}
                      className="h-7 px-2"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => handlePlayRecording(recording.streamId)}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Play
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {onViewMore && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewMore}
                  className="w-full justify-between"
                >
                  Manage All Recordings
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Recording Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Recording Details</DialogTitle>
            <DialogDescription>
              View details and download recording
            </DialogDescription>
          </DialogHeader>
          {selectedRecording && (
            <StreamRecordingDetails
              streamId={selectedRecording.streamId}
              recordingId={selectedRecording.id}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
